/**
 * useIdleDetector — derives an auto-status from user activity.
 *
 * Listens for mouse/keyboard/touch/scroll events. Returns one of:
 *   'online' — recent activity
 *   'away'   — no activity for AWAY_AFTER_MS (default 5 min)
 *   'idle'   — no activity for IDLE_AFTER_MS (default 15 min)
 *   'offline'— Page Visibility says tab is hidden
 */
import { useEffect, useState } from 'react';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

export const useIdleDetector = ({
  awayAfterMs = 5 * 60 * 1000,
  idleAfterMs = 15 * 60 * 1000,
} = {}) => {
  const [status, setStatus] = useState('online');

  useEffect(() => {
    let lastActive = Date.now();
    let timer = null;

    const recompute = () => {
      if (typeof document !== 'undefined' && document.hidden) {
        setStatus('offline');
        return;
      }
      const idleMs = Date.now() - lastActive;
      if (idleMs >= idleAfterMs) setStatus('idle');
      else if (idleMs >= awayAfterMs) setStatus('away');
      else setStatus('online');
    };

    const onActivity = () => {
      lastActive = Date.now();
      recompute();
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', recompute);

    timer = setInterval(recompute, 30_000);
    recompute();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', recompute);
      if (timer) clearInterval(timer);
    };
  }, [awayAfterMs, idleAfterMs]);

  return status;
};

export default useIdleDetector;
